import React, { useEffect, useRef, useState } from 'react';
import { uploadModeratedMedia } from '../utils/moderatedUpload';

type Props = {
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  /** When provided, the user can grab a frame from this video as the thumbnail. */
  videoFile?: File | null;
};

const MAX_BYTES = 5 * 1024 * 1024;
const MAX_FRAME_WIDTH = 720;

const ThumbnailPicker: React.FC<Props> = ({ file, onChange, disabled, videoFile }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [preview, setPreview] = useState('');
  const [frameOpen, setFrameOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [grabbing, setGrabbing] = useState(false);

  const hasVideo = !!videoFile && ((videoFile.type || '').startsWith('video/') || /\.(mp4|mov|webm|m4v|3gp|mkv)$/i.test(videoFile.name || ''));

  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!frameOpen || !hasVideo || !videoFile) { setVideoUrl(''); return; }
    const url = URL.createObjectURL(videoFile);
    setVideoUrl(url);
    setDuration(0);
    setTime(0);
    return () => URL.revokeObjectURL(url);
  }, [frameOpen, hasVideo, videoFile]);

  useEffect(() => {
    if (!hasVideo) setFrameOpen(false);
  }, [hasVideo]);

  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    e.target.value = '';
    if (!f) return;
    if (!f.type.startsWith('image/')) { alert('Thumbnail must be an image.'); return; }
    if (f.size > MAX_BYTES) { alert('Thumbnail must be under 5MB.'); return; }
    onChange(f);
  };

  const seek = (t: number) => {
    setTime(t);
    if (videoRef.current) videoRef.current.currentTime = t;
  };

  const useFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) { alert('The video is still loading. Try again in a moment.'); return; }
    setGrabbing(true);
    try {
      const scale = Math.min(1, MAX_FRAME_WIDTH / v.videoWidth);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(v.videoWidth * scale);
      canvas.height = Math.round(v.videoHeight * scale);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no-canvas');
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setGrabbing(false);
        if (!blob) { alert('Could not capture this frame. Try another one.'); return; }
        onChange(new File([blob], `thumbnail-${Date.now()}.jpg`, { type: 'image/jpeg' }));
        setFrameOpen(false);
      }, 'image/jpeg', 0.85);
    } catch {
      setGrabbing(false);
      alert('Could not capture this frame. Try another one.');
    }
  };

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div style={{ margin: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pick} />
        {preview ? (
          <>
            <img src={preview} alt="Thumbnail" style={{ width: 48, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #E0E0E0' }} />
            <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} style={btn}>Change thumbnail</button>
            <button type="button" disabled={disabled} onClick={() => onChange(null)} style={{ ...btn, color: '#C62828' }}>Remove</button>
          </>
        ) : (
          <button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} style={btn}>🖼️ Add thumbnail (optional)</button>
        )}
        {hasVideo && !frameOpen && (
          <button type="button" disabled={disabled} onClick={() => setFrameOpen(true)} style={btn}>🎞️ Pick from video</button>
        )}
      </div>

      {frameOpen && videoUrl && (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #CFE3E3', borderRadius: 12, background: '#F7FBFB', maxWidth: 360 }}>
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              const dur = Number.isFinite(d) ? d : 0;
              setDuration(dur);
              const start = Math.min(1, dur / 2);
              e.currentTarget.currentTime = start;
              setTime(start);
            }}
            style={{ width: '100%', maxHeight: 320, borderRadius: 8, background: '#000', display: 'block', objectFit: 'contain' }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.05}
              value={time}
              disabled={!duration || disabled}
              onChange={(e) => seek(Number(e.target.value))}
              aria-label="Choose a frame"
              style={{ flex: 1, minWidth: 0 }}
            />
            <span style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>{fmt(time)} / {fmt(duration)}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button type="button" disabled={disabled || grabbing || !duration} onClick={useFrame} style={{ ...btn, background: '#0B4F51', color: '#fff' }}>
              {grabbing ? 'Capturing…' : '✓ Use this frame'}
            </button>
            <button type="button" onClick={() => setFrameOpen(false)} style={btn}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

const btn: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 999,
  border: '1px solid #CFE3E3',
  background: '#F3FAFA',
  color: '#0B4F51',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/** Uploads a thumbnail image through the moderated pipeline; returns the key and its proof. */
export async function uploadThumbnail(file: File) {
  const upload = await uploadModeratedMedia({
    folder: 'thumbnails',
    field: 'thumbnailURL',
    file,
    contentType: file.type || 'image/jpeg',
  });
  return { thumbnailURL: upload.objectKey, proof: upload.proof };
}

export default ThumbnailPicker;
